import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  cn,
  SimpleSelect,
  TextInput,
  Disclosure,
  SimpleSummary,
} from 'corso-admin-ui';
import {
  CrewClaimReasonCategoryCode,
  crewClaimReasonCategoryCodeName,
} from 'corso-types';
import { ComponentType, FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ClaimReason,
  ClaimReasonCreate,
  claimReasonCreate,
  ClaimReasonUpdate,
} from '~/types';
import useClaimReasonCategories from './hooks/useClaimReasonCategories';

type ClaimReasonDetailCreate = ClaimReasonUpdate['claimReasonDetails'][number];
type ClaimReasonDetail = ClaimReason['claimReasonDetails'][number];

function ReasonCreate({
  onCreate,
  onCancel,
}: {
  onCreate: (reason: ClaimReasonCreate) => void;
  onCancel: () => void;
}) {
  const categories = useClaimReasonCategories();
  const { control, register, handleSubmit } = useForm<ClaimReasonCreate>({
    resolver: zodResolver(claimReasonCreate),
    defaultValues: {
      name: '',
      category: {
        code: CrewClaimReasonCategoryCode.notAsExpected,
        name: crewClaimReasonCategoryCodeName[
          CrewClaimReasonCategoryCode.notAsExpected
        ],
      },
      claimReasonDetails: [],
    },
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => onCreate(values))(event).catch(console.error);
  };

  return (
    <form
      className="flex flex-col flex-wrap gap-2 md:flex-row md:items-center"
      onSubmit={submitHandler}
    >
      <TextInput
        className="flex-1"
        id="claim-reason-name"
        label="Claim Reason Name"
        placeholder="Reason" // ? possibly address if not visually clear enough
        labelVisuallyHidden
        required
        {...register('name')}
      />
      <Controller
        control={control}
        name="category"
        render={({ field: { value, onChange } }) => (
          <SimpleSelect
            label="Category"
            labelVisuallyHidden
            // TODO consider allowing any type for the `value`, may require memoization of options
            options={categories
              .filter((v) => v.code !== 'Right_To_Withdraw')
              .map((category) => ({
                value: category.code,
                label: category.name,
              }))}
            value={value.code}
            onChange={(code) =>
              onChange(categories.find((c) => c.code === code))
            }
          />
        )}
      />
      <div className="grid grid-cols-2 gap-2">
        <Action type="submit" icon="add" accessibilityLabel="Add Reason" />
        <Action icon="remove" accessibilityLabel="Cancel" onClick={onCancel} />
      </div>
    </form>
  );
}

function ReasonDetailsCreate({
  onCreate,
  onCancel,
}: {
  onCreate: (reasonDetail: ClaimReasonDetailCreate) => void;
  onCancel: () => void;
}) {
  const { register, handleSubmit, reset } = useForm<ClaimReasonDetailCreate>({
    defaultValues: {
      name: '',
      isDeleted: false,
    },
  });
  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => {
      onCreate(values);
      reset();
    })(event).catch(console.error);
  };
  return (
    <form
      className="flex flex-col gap-2 sm:flex-row sm:items-center"
      onSubmit={submitHandler}
    >
      <TextInput
        className="flex-1"
        id="claim-reason-detail-name"
        label="Claim Reason Detail Name"
        placeholder="Reason Detail" // ? possibly address if not visually clear enough
        labelVisuallyHidden
        required
        {...register('name', { required: true })}
      />
      <div className="grid grid-cols-2 gap-2">
        <Action
          type="submit"
          icon="add"
          accessibilityLabel="Add Reason Detail"
        />
        <Action
          icon="close"
          variant="ghost"
          accessibilityLabel="Cancel"
          onClick={onCancel}
        />
      </div>
    </form>
  );
}

function ReasonDetailsList({
  details,
  onChange,
  editable = false,
  dataTestId,
}: {
  dataTestId?: string;
  details: ClaimReasonDetail[];
  editable?: boolean;
  onChange: (reasonDetail: ClaimReasonDetail | ClaimReasonDetailCreate) => void;
}) {
  const [showForm, setShowForm] = useState(false);

  const removeDetail = (reasonDetail: ClaimReasonDetail) => () =>
    onChange({ ...reasonDetail, isDeleted: true });
  const addDetail = (reasonDetail: ClaimReasonDetailCreate) => {
    onChange(reasonDetail);
    setShowForm(false);
  };

  return (
    <>
      <ul data-testid={dataTestId} className="pb-2">
        {details.map((detail) => (
          <li
            key={detail.id}
            className="rounded px-2 py-1 text-sm odd:bg-neutral-100"
          >
            <div className="flex items-center justify-between">
              {detail.name}
              {editable && (
                <Action
                  accessibilityLabel={`Remove Reason Detail: ${detail.name}`}
                  icon="close"
                  variant="ghost"
                  onClick={removeDetail(detail)}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
      {editable && (
        <div className="pr-2">
          {showForm ? (
            <ReasonDetailsCreate
              onCreate={addDetail}
              onCancel={() => setShowForm(false)}
            />
          ) : (
            // ? possibly make a small variant of the classic button to match the icon action button size
            <Action icon="add" onClick={() => setShowForm(true)}>
              Add Reason Detail
            </Action>
          )}
        </div>
      )}
    </>
  );
}

type ReasonListProps = {
  dataTestId?: string;
  className?: string;
  reasons?: ClaimReason[];
  action?: ComponentType<{ reason: ClaimReason }>;
} & (
  | {
      editable?: true;
      onChange: (reason: ClaimReasonCreate | ClaimReasonUpdate) => void;
    }
  | {
      editable?: false;
      onChange?: never;
    }
);

/** Represents a series of reasons and their details in a mutable way. */
export default function ReasonList({
  className,
  reasons = [],
  onChange,
  editable = false,
  action: ListAction,
  dataTestId,
}: ReasonListProps) {
  const [showForm, setShowForm] = useState(false);

  const addReason = (reason: ClaimReasonCreate) => {
    onChange?.(reason);
    setShowForm(false);
  };
  const removeReason = (reason: ClaimReason) => () =>
    onChange?.({
      ...reason,
      isDeleted: true,
      claimReasonDetails: reason.claimReasonDetails.map((detail) => ({
        ...detail,
        isDeleted: true,
      })),
    });

  const onReasonDetailChange =
    (reason: ClaimReason) =>
    (reasonDetail: ClaimReasonDetailCreate | ClaimReasonDetail) => {
      if (!reasonDetail.id) {
        // create new reason detail
        onChange?.({
          ...reason,
          claimReasonDetails: [...reason.claimReasonDetails, reasonDetail],
        });
      } else {
        // update existing reason detail
        onChange?.({
          ...reason,
          claimReasonDetails: reason.claimReasonDetails.map((detail) =>
            detail.id === reasonDetail.id ? reasonDetail : detail,
          ),
        });
      }
    };

  return (
    <ul
      data-testid={dataTestId}
      className={cn('divide-y rounded-sm border', className)}
    >
      {reasons.map((reason) => (
        <li key={reason.id}>
          <Disclosure
            renderSummary={
              <SimpleSummary>
                <span className="text-sm font-medium">{reason.name}</span>
                <Badge>{reason.category.name}</Badge>
                <div className="flex grow justify-end">
                  {ListAction && <ListAction reason={reason} />}
                  {editable && reason.category.code !== 'Right_To_Withdraw' && (
                    <Action
                      variant="ghost"
                      accessibilityLabel={`Remove ${reason.name}`}
                      icon="remove"
                      onClick={removeReason(reason)}
                    />
                  )}
                </div>
              </SimpleSummary>
            }
          >
            <div className="pb-4 pl-4">
              <ReasonDetailsList
                dataTestId={`reason-details-list-${reason.id}`}
                editable={
                  editable && reason.category.code !== 'Right_To_Withdraw'
                }
                details={reason.claimReasonDetails}
                onChange={onReasonDetailChange(reason)}
              />
            </div>
          </Disclosure>
        </li>
      ))}
      {editable && (
        <li className="p-2">
          {showForm ? (
            <ReasonCreate
              onCreate={addReason}
              onCancel={() => setShowForm(false)}
            />
          ) : (
            <Action icon="add" onClick={() => setShowForm(true)}>
              Add Reason
            </Action>
          )}
        </li>
      )}
    </ul>
  );
}
